/**
 * Dashboard Monitoramento Externo — exibição para TV com atualização via SSE (EventSource).
 * Dados reais do backend em /api/monitoramento/externo/stream (token via query).
 * Clique em activity (OS concluída) abre popover com detalhe da OS.
 */

import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  CalendarCheck,
  Car,
  ContactRound,
  FastForward,
  Hourglass,
  Timer,
  Watch
} from 'lucide-react';
import { getApiBaseUrl } from '../../../utils/apiConfig';
import { clearAuthSession, hasAuthSession } from '../../../utils/authSession';
import {
  TECH_STATUS_TYPES,
  getTechnicianDisplayStatus,
  statusSortRank
} from '../utils/monitoramentoStatusUtils';
import {
  MonitoramentoModernTooltip,
  collectOsByCondition
} from './MonitoramentoModernTooltip';
import './MonitoramentoExternoDashboard.css';

const HOUR_START = 7;
const HOUR_END = 19;
const HOURS = Array.from({ length: HOUR_END - HOUR_START + 1 }, (_, i) => HOUR_START + i);

const STATUS_TYPES = {
  ...TECH_STATUS_TYPES,
  deslocamento_trajeto: { label: 'Desloc.', color: '#4B5563', dot: '#6B7280' },
  concluida: { label: 'Concluída', color: '#1F7A5C', dot: '#1F9A73' },
  almoco: { label: 'Almoço', color: '#f43f5e', dot: '#fb7185' }
};

const KPI_ICON_SIZE = 17;
const KPI_ICON_STROKE = 2;

function pctOfHour(h) {
  return Math.max(0, Math.min(100, ((h - HOUR_START) / (HOUR_END - HOUR_START)) * 100));
}

function getHourMarkStyle(h) {
  const left = pctOfHour(h);
  let transform = 'translateX(-50%)';
  if (h === HOUR_START) transform = 'none';
  if (h === HOUR_END) transform = 'translateX(-100%)';
  return { left: `${left}%`, transform };
}

function getTechnicianInitials(name) {
  if (!name) return '?';
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

function formatTechnicianDisplayName(name) {
  if (!name) return '';
  return String(name)
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function buildOsResumoDisplay(numeroOS, row, context = null) {
  if (!numeroOS) return null;
  return {
    OS: numeroOS,
    CLIENTE: row?.CLIENTE ?? null,
    SERIE: row?.SERIE ?? null,
    PATRIMONIO: row?.PATRIMONIO ?? null,
    __resumo: true,
    __tempoAtual: context?.tempo ?? null,
    __statusLabel: context?.statusLabel ?? null,
    __dataInicio: context?.dataInicio ?? null
  };
}

function MonitoramentoKpiCard({ icon: Icon, value, extra, label, valueTone = 'default' }) {
  return (
    <div className="monitoramento-externo-kpi">
      <div className="monitoramento-externo-kpi-main">
        <div className="monitoramento-externo-kpi-values">
          <span className={`monitoramento-externo-kpi-value monitoramento-externo-kpi-value--${valueTone}`}>{value}</span>
          {extra ? <span className="monitoramento-externo-kpi-extra">{extra}</span> : null}
        </div>
        <div className="monitoramento-externo-kpi-label">{label}</div>
      </div>
      {Icon ? (
        <div className="monitoramento-externo-kpi-icon-box" aria-hidden>
          <Icon size={KPI_ICON_SIZE} strokeWidth={KPI_ICON_STROKE} />
        </div>
      ) : null}
    </div>
  );
}

const INITIAL_KPIS = {
  ativos: 0,
  total: 0,
  ativosLabel: 'em campo hoje',
  osConcluidas: 0,
  retornosDia: 0,
  meta: 0,
  metaPct: 0,
  osSemEncaminhamento: 0
};

/** Converte "mm:ss" em total de segundos; ignora "—". */
function parseTempoMedioSegundos(sla) {
  if (!sla || sla === '—') return null;
  const parts = sla.trim().split(':');
  if (parts.length < 2) return null;
  const a = parseInt(parts[0], 10);
  const b = parseInt(parts[1], 10);
  if (Number.isNaN(a) || Number.isNaN(b)) return null;
  return a * 60 + b;
}

/** Converte "hh:mm" em minutos totais; null se inválido. */
function parseProduzidoMinutos(str) {
  if (!str || str === '—') return null;
  const parts = String(str).trim().split(':');
  if (parts.length < 2) return null;
  const h = parseInt(parts[0], 10);
  const m = parseInt(parts[1], 10);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return h * 60 + m;
}

/** Ordena: 1) Em serviço, 2) Deslocamento, 3) demais; dentro de cada grupo por Produzido (maior primeiro). */
function sortTechnicians(list, isLive = true) {
  if (!list || list.length === 0) return list;
  return [...list].sort((a, b) => {
    const ra = statusSortRank(getTechnicianDisplayStatus(a, isLive));
    const rb = statusSortRank(getTechnicianDisplayStatus(b, isLive));
    if (ra !== rb) return ra - rb;
    const minA = parseProduzidoMinutos(a.tempoProduzido) ?? -1;
    const minB = parseProduzidoMinutos(b.tempoProduzido) ?? -1;
    return minB - minA;
  });
}

/** Média dos tempos médios dos técnicos (coluna Tempo médio), formatada em mm:ss. */
function mediaTempoMedioAtendimento(technicians) {
  const segundos = technicians
    .map((t) => parseTempoMedioSegundos(t.sla))
    .filter((s) => s != null);
  if (segundos.length === 0) return '—';
  const mediaSeg = segundos.reduce((a, b) => a + b, 0) / segundos.length;
  const m = Math.floor(mediaSeg / 60);
  const s = Math.round(mediaSeg % 60);
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function formatHoraAtual() {
  const d = new Date();
  return [
    String(d.getHours()).padStart(2, '0'),
    String(d.getMinutes()).padStart(2, '0'),
    String(d.getSeconds()).padStart(2, '0')
  ].join(':');
}

function clamp(n, min, max) {
  return Math.min(max, Math.max(min, n));
}

function getNowPositionPct() {
  const d = new Date();
  const totalMin = (HOUR_END - HOUR_START) * 60;
  const nowMin = (d.getHours() - HOUR_START) * 60 + d.getMinutes() + d.getSeconds() / 60;
  const clamped = clamp(nowMin, 0, totalMin);
  return (clamped / totalMin) * 100;
}

const TOTAL_STRIP_MIN = (HOUR_END - HOUR_START) * 60;

const LUNCH_TZ = 'America/Sao_Paulo';

/** TB02130_DATA (ISO local) → minutos desde 7h na faixa da timeline. */
function minutesFrom7hIso(isoStr) {
  if (!isoStr) return null;
  const m = String(isoStr).match(/T(\d{1,2}):(\d{2})(?::(\d{2}))?/);
  if (!m) return null;
  const h = parseInt(m[1], 10);
  const min = parseInt(m[2], 10);
  const sec = m[3] != null ? parseInt(m[3], 10) : 0;
  if (Number.isNaN(h) || Number.isNaN(min)) return null;
  return h * 60 + min + (Number.isNaN(sec) ? 0 : sec) / 60 - HOUR_START * 60;
}

/**
 * Almoço: ISO UTC global → minutos desde 7h em America/Sao_Paulo.
 * Strings sem Z são tratadas como UTC (campo started_at/cancelled_at).
 */
function parseLunchUtcDate(isoStr) {
  if (isoStr == null || isoStr === '') return null;
  if (isoStr instanceof Date) return Number.isNaN(isoStr.getTime()) ? null : isoStr;
  let s = String(isoStr).trim();
  if (!s) return null;
  if (/^\d{4}-\d{2}-\d{2}T\d{1,2}:\d{2}/.test(s) && !/(Z|[+-]\d{2}:?\d{2})$/i.test(s)) {
    s = `${s}Z`;
  }
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}

function minutesFrom7hSaoPauloInstant(isoStr) {
  const d = parseLunchUtcDate(isoStr);
  if (!d) return null;
  const fmt = new Intl.DateTimeFormat('en-CA', {
    timeZone: LUNCH_TZ,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  });
  const parts = Object.fromEntries(fmt.formatToParts(d).map((p) => [p.type, p.value]));
  const h = Number(parts.hour === '24' ? 0 : parts.hour);
  const min = Number(parts.minute);
  const sec = Number(parts.second);
  if (!Number.isFinite(h) || !Number.isFinite(min)) return null;
  return h * 60 + min + (Number.isFinite(sec) ? sec : 0) / 60 - HOUR_START * 60;
}

function getNowMinutesFrom7hSaoPaulo() {
  return minutesFrom7hSaoPauloInstant(new Date().toISOString());
}

/** HH:mm do instante almoço em America/Sao_Paulo. */
function formatLunchTimeHhMm(val) {
  const d = parseLunchUtcDate(val);
  if (!d) return '—';
  const fmt = new Intl.DateTimeFormat('en-GB', {
    timeZone: LUNCH_TZ,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });
  return fmt.format(d);
}

function getNowMinutesFrom7h() {
  const d = new Date();
  return d.getHours() * 60 + d.getMinutes() + d.getSeconds() / 60 - HOUR_START * 60;
}

function isFilled(val) {
  if (val == null) return false;
  const s = String(val).trim();
  return s !== '' && s.toLowerCase() !== 'null' && s.toLowerCase() !== 'undefined';
}

/** Formata valor de data para dd/MM/yyyy (sem alterar fuso). */
function formatDateBr(val) {
  if (val == null) return '—';
  const d = val instanceof Date ? val : new Date(val);
  if (Number.isNaN(d.getTime())) return '—';
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
}

/** Formata hora para hh:mm (string "HH:mm" ou Date). */
function formatTimeHhMm(val) {
  if (val == null) return '—';
  if (typeof val === 'string' && /^\d{1,2}:\d{2}/.test(val.trim())) {
    const parts = val.trim().split(':');
    const h = parseInt(parts[0], 10);
    const m = parseInt(parts[1], 10);
    return `${String(isNaN(h) ? 0 : h).padStart(2, '0')}:${String(isNaN(m) ? 0 : m).padStart(2, '0')}`;
  }
  const d = val instanceof Date ? val : new Date(val);
  if (Number.isNaN(d.getTime())) return '—';
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/** Calcula duração em minutos entre (dataIni,horaIni) e (dataFim,horaFim); retorna string hh:mm. */
function formatDuracao(dataIni, horaIni, dataFim, horaFim) {
  const parseDt = (d, t) => {
    let date = d == null ? null : (d instanceof Date ? d : new Date(d));
    if (date && Number.isNaN(date.getTime())) date = null;
    let h = 0, m = 0;
    if (typeof t === 'string' && /^\d{1,2}:\d{2}/.test(t && t.trim())) {
      const p = t.trim().split(':');
      h = parseInt(p[0], 10) || 0;
      m = parseInt(p[1], 10) || 0;
    } else if (t instanceof Date) {
      h = t.getHours();
      m = t.getMinutes();
    }
    if (!date) return null;
    const out = new Date(date.getFullYear(), date.getMonth(), date.getDate(), h, m, 0);
    return Number.isNaN(out.getTime()) ? null : out;
  };
  const start = parseDt(dataIni, horaIni);
  const end = parseDt(dataFim, horaFim);
  if (!start || !end) return '—';
  const mins = Math.max(0, Math.round((end - start) / 60000));
  const h = Math.floor(mins / 60);
  const min = mins % 60;
  return `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
}

/** Duração total entre dois instantes (ISO ou Date) em hh:mm. */
function formatDeslocDuracaoHhMm(inicioVal, fimVal) {
  if (inicioVal == null || fimVal == null) return '—';
  const start = inicioVal instanceof Date ? inicioVal : new Date(inicioVal);
  const end = fimVal instanceof Date ? fimVal : new Date(fimVal);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return '—';
  const mins = Math.max(0, Math.round((end - start) / 60000));
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/** TB02115_PREVENTIVA (1 caractere) → legenda no popover */
const OS_TIPO_PREVENTIVA_LABEL = {
  E: 'estoque',
  B: 'Balcão',
  A: 'Aferição',
  R: 'Retorno/Recarga',
  D: 'Desinstalação',
  I: 'Instalação',
  S: 'Preventiva',
  N: 'Corretiva',
};

function labelOsTipoPreventiva(raw) {
  if (raw == null || raw === '') return null;
  const c = String(raw).trim().toUpperCase().charAt(0);
  return OS_TIPO_PREVENTIVA_LABEL[c] || null;
}

function OsDetalhePopover({ anchorRect, data, loading, error, onClose, popoverRef }) {
  if (!anchorRect) return null;

  const isResumo = Boolean(data?.__resumo);
  const tipoOsLabel = data && !loading && !isResumo ? labelOsTipoPreventiva(data.TB02115_PREVENTIVA) : null;
  const condicaoLabel = data?.TB01055_NOME ?? '—';

  const style = {
    position: 'fixed',
    left: Math.max(12, Math.min(anchorRect.left, typeof window !== 'undefined' ? window.innerWidth - 291 - 12 : anchorRect.left)),
    top: anchorRect.bottom + 8,
    zIndex: 20050,
    width: 291
  };

  const content = (
    <div
      ref={popoverRef}
      className="monitoramento-externo-os-popover"
      style={style}
      role="dialog"
      aria-labelledby="monitoramento-os-popover-title"
      aria-modal="false"
    >
      <div className="monitoramento-externo-os-popover-inner">
        {loading && (
          <div className="monitoramento-externo-os-popover-loading">Carregando…</div>
        )}
        {error && (
          <div className="monitoramento-externo-os-popover-error">{error}</div>
        )}
        {data && !loading && (
          <>
            <div className="monitoramento-externo-os-popover-header">
              <div className="monitoramento-externo-os-popover-header-left">
                <h3 id="monitoramento-os-popover-title" className="monitoramento-externo-os-popover-os">
                  OS {data.OS != null ? String(data.OS) : '—'}
                </h3>
                {isResumo ? (
                  <span className="monitoramento-externo-os-popover-os-tipo">EM SERVIÇO</span>
                ) : tipoOsLabel ? (
                  <span className="monitoramento-externo-os-popover-os-tipo">{tipoOsLabel}</span>
                ) : null}
              </div>
              <button type="button" className="monitoramento-externo-os-popover-close" onClick={onClose} aria-label="Fechar">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
              </button>
            </div>

            <div className="monitoramento-externo-os-popover-body">
              <section className="monitoramento-externo-os-popover-section">
                <div className="monitoramento-externo-os-popover-label">Cliente</div>
                <div className="monitoramento-externo-os-popover-value">
                  {isResumo
                    ? (data.CLIENTE ? String(data.CLIENTE).trim() : '—')
                    : ([data.TB02122_CONTRATO, data.TB01008_NOME].filter(Boolean).map(String).join(' ') || '—')}
                </div>
              </section>

              {isResumo ? (
                <section className="monitoramento-externo-os-popover-section">
                  <div className="monitoramento-externo-os-popover-field">
                    <div className="monitoramento-externo-os-popover-label">Série/patrimônio</div>
                    <div className="monitoramento-externo-os-popover-value monitoramento-externo-os-popover-value--mono">
                      {[data.SERIE, data.PATRIMONIO].filter(Boolean).map(String).join(' ') || '—'}
                    </div>
                  </div>
                </section>
              ) : (
                <section className="monitoramento-externo-os-popover-section">
                  <div className="monitoramento-externo-os-popover-field">
                    <div className="monitoramento-externo-os-popover-label">Equipamento</div>
                    <div className="monitoramento-externo-os-popover-value">{data.TB01010_NOME ?? '—'}</div>
                  </div>
                  <div className="monitoramento-externo-os-popover-field">
                    <div className="monitoramento-externo-os-popover-label">Série/patrimônio</div>
                    <div className="monitoramento-externo-os-popover-value monitoramento-externo-os-popover-value--mono">
                      {[data.TB02122_NUMSERIE, data.TB02112_PAT].filter(Boolean).map(String).join(' ') || '—'}
                    </div>
                  </div>
                </section>
              )}

              <section className="monitoramento-externo-os-popover-section">
                {isResumo ? (
                  <>
                    {data.__dataInicio && (
                      <div className="monitoramento-externo-os-popover-field">
                        <div className="monitoramento-externo-os-popover-label">Início do atendimento</div>
                        <div className="monitoramento-externo-os-popover-value monitoramento-externo-os-popover-value--mono">
                          {formatTimeHhMm(data.__dataInicio)}
                          {data.__tempoAtual ? (
                            <span className="monitoramento-externo-os-popover-duracao"> ({data.__tempoAtual})</span>
                          ) : null}
                        </div>
                      </div>
                    )}
                    {data.__statusLabel && (
                      <div className="monitoramento-externo-os-popover-field">
                        <div className="monitoramento-externo-os-popover-label">Status</div>
                        <div className="monitoramento-externo-os-popover-value">{data.__statusLabel}</div>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <div className="monitoramento-externo-os-popover-field">
                      <div className="monitoramento-externo-os-popover-label">Tempo de atendimento</div>
                      <div className="monitoramento-externo-os-popover-value monitoramento-externo-os-popover-value--mono">
                        {formatDateBr(data.TB02122_DATAINI)} {formatTimeHhMm(data.TB02122_HORAINI)} → {formatDateBr(data.TB02122_DATAFIM)} {formatTimeHhMm(data.TB02122_HORAFIM)}{' '}
                        <span className="monitoramento-externo-os-popover-duracao">({data.__tempoAtual || formatDuracao(data.TB02122_DATAINI, data.TB02122_HORAINI, data.TB02122_DATAFIM, data.TB02122_HORAFIM)})</span>
                      </div>
                    </div>
                    <div className="monitoramento-externo-os-popover-field">
                      <div className="monitoramento-externo-os-popover-label">Condição</div>
                      <div className="monitoramento-externo-os-popover-value">{condicaoLabel}</div>
                    </div>
                    {data.REINCIDENCIA != null && String(data.REINCIDENCIA).trim() !== '' && (
                      <div className="monitoramento-externo-os-popover-field">
                        <div className="monitoramento-externo-os-popover-label">Reincidência</div>
                        <div className="monitoramento-externo-os-popover-value">{String(data.REINCIDENCIA)}</div>
                      </div>
                    )}
                  </>
                )}
              </section>
            </div>
          </>
        )}
      </div>
    </div>
  );

  return typeof document !== 'undefined' ? createPortal(content, document.body) : null;
}

function DeslocamentoAndamentoPopover({ anchorRect, numeroOS, data, loading, error, context, onClose, popoverRef }) {
  if (!anchorRect) return null;

  const tIni = context?.dataInicio != null ? formatTimeHhMm(context.dataInicio) : '—';
  const tFim = 'agora';
  const dur = context?.tempo || (context?.dataInicio ? formatDeslocDuracaoHhMm(context.dataInicio, new Date()) : '—');
  const horarioText = `${tIni} ~ ${tFim} > ${dur}`;

  const style = {
    position: 'fixed',
    left: Math.max(12, Math.min(anchorRect.left, typeof window !== 'undefined' ? window.innerWidth - 224 - 12 : anchorRect.left)),
    top: anchorRect.bottom + 8,
    zIndex: 20060,
    width: 224
  };

  const content = (
    <div
      ref={popoverRef}
      className="monitoramento-externo-desloc-tip"
      style={style}
      role="dialog"
      aria-modal="false"
    >
      <div className="monitoramento-externo-desloc-tip-header">
        <div className="monitoramento-externo-desloc-tip-header-left">
          <span className="monitoramento-externo-desloc-tip-kicker">Deslocamento</span>
          <span className="monitoramento-externo-desloc-tip-os">OS {numeroOS || '—'}</span>
        </div>
        <button type="button" className="monitoramento-externo-os-popover-close" onClick={onClose} aria-label="Fechar">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
        </button>
      </div>
      <div className="monitoramento-externo-desloc-tip-body">
        {loading && (
          <div className="monitoramento-externo-os-popover-loading">Carregando…</div>
        )}
        {error && (
          <div className="monitoramento-externo-os-popover-error">{error}</div>
        )}
        {!loading && (
          <>
            <div className="monitoramento-externo-desloc-tip-section">
              <div className="monitoramento-externo-desloc-tip-label">Cliente</div>
              <div className="monitoramento-externo-desloc-tip-cliente">
                {data?.CLIENTE ? String(data.CLIENTE).trim() : '—'}
              </div>
            </div>
            <div className="monitoramento-externo-desloc-tip-section">
              <div className="monitoramento-externo-desloc-tip-label">Intervalo</div>
              <div className="monitoramento-externo-desloc-tip-horario">{horarioText}</div>
            </div>
          </>
        )}
      </div>
    </div>
  );

  return typeof document !== 'undefined' ? createPortal(content, document.body) : null;
}

function ActivityIcon({ name, size = 16 }) {
  const common = {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 2,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    'aria-hidden': 'true',
    focusable: 'false'
  };

  if (name === 'wrench') {
    return (
      <svg {...common}>
        <path d="M14.7 6.3a4.1 4.1 0 0 0-5.4 5.4l-6 6a2 2 0 1 0 2.8 2.8l6-6a4.1 4.1 0 0 0 5.4-5.4l-2.6 2.6-3-3Z" />
      </svg>
    );
  }

  if (name === 'bell') {
    return (
      <svg {...common}>
        <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
        <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
      </svg>
    );
  }

  if (name === 'car') {
    return (
      <svg {...common}>
        <path d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9C18.7 10.6 16 10 16 10s-1.3-1.5-2.2-2.3c-.5-.4-1.1-.7-1.8-.7H5c-.6 0-1.1.4-1.4.9l-1.4 2.9A3.7 3.7 0 0 0 2 12v4c0 .6.4 1 1 1h2" />
        <circle cx="7" cy="17" r="2" />
        <path d="M9 17h6" />
        <circle cx="17" cy="17" r="2" />
      </svg>
    );
  }

  if (name === 'headset') {
    return (
      <svg {...common}>
        <path d="M4 12a8 8 0 0 1 16 0" />
        <path d="M4 15v-3" />
        <path d="M20 15v-3" />
        <rect x="2" y="13" width="4" height="6" rx="2" />
        <rect x="18" y="13" width="4" height="6" rx="2" />
        <path d="M18 19a3 3 0 0 1-3 3h-3" />
      </svg>
    );
  }

  if (name === 'coffee') {
    return (
      <svg {...common}>
        <path d="M17 8h1a4 4 0 1 1 0 8h-1" />
        <path d="M3 8h14v9a4 4 0 0 1-4 4H7a4 4 0 0 1-4-4Z" />
        <line x1="6" x2="6" y1="2" y2="4" />
        <line x1="10" x2="10" y1="2" y2="4" />
        <line x1="14" x2="14" y1="2" y2="4" />
      </svg>
    );
  }

  return (
    <svg {...common}>
      <circle cx="12" cy="12" r="10" />
      <path d="m9 12 2 2 4-4" />
    </svg>
  );
}

function MonitoramentoOsAndamentoBar({ variant, os, dataInicio, tempo, nowTick, onOsClick, label }) {
  const ref = useRef(null);
  const [showOs, setShowOs] = useState(false);
  const [showMoreHint, setShowMoreHint] = useState(false);
  const isDesloc = variant === 'deslocamento';
  const isAlmoco = variant === 'almoco';

  const startMin = useMemo(
    () => (isAlmoco ? minutesFrom7hSaoPauloInstant(dataInicio) : minutesFrom7hIso(dataInicio)),
    [dataInicio, isAlmoco]
  );
  const endMin = useMemo(() => {
    void nowTick;
    if (isAlmoco) {
      return clamp(getNowMinutesFrom7hSaoPaulo(), 0, TOTAL_STRIP_MIN);
    }
    const now = getNowMinutesFrom7h();
    return clamp(now, 0, TOTAL_STRIP_MIN);
  }, [nowTick, isAlmoco]);

  const startClamp = startMin != null ? clamp(startMin, 0, TOTAL_STRIP_MIN) : null;
  const endClamp =
    startClamp != null
      ? Math.max(startClamp + 0.5, clamp(endMin, startClamp, TOTAL_STRIP_MIN))
      : null;

  const leftPct = startClamp != null && endClamp != null ? (startClamp / TOTAL_STRIP_MIN) * 100 : null;
  const widthPct =
    startClamp != null && endClamp != null && endClamp > startClamp
      ? ((endClamp - startClamp) / TOTAL_STRIP_MIN) * 100
      : null;

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const calc = () => {
      const width = el.clientWidth || 0;
      setShowOs(width >= 40);
      setShowMoreHint(width >= 72);
    };
    calc();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => calc());
    ro.observe(el);
    return () => ro.disconnect();
  }, [widthPct]);

  if (leftPct == null || widthPct == null || widthPct <= 0) return null;

  const ctxLabel = isAlmoco ? 'em almoço' : isDesloc ? 'em deslocamento' : 'em serviço';
  const title = isAlmoco
    ? `Almoço${dataInicio ? ` — ${formatLunchTimeHhMm(dataInicio)}` : ''}${tempo ? ` · ${tempo}` : ''}`
    : `OS ${os}${tempo ? ` — ${tempo} ${ctxLabel}` : ` — ${ctxLabel}`}`;
  const stripClass = isAlmoco
    ? 'monitoramento-externo-os-em-almoco-strip'
    : isDesloc
      ? 'monitoramento-externo-os-em-deslocamento-strip'
      : 'monitoramento-externo-os-em-servico-strip';
  const clickable = typeof onOsClick === 'function';
  const displayLabel = isAlmoco ? (label || 'Almoço') : os;

  const handleClick = (event) => {
    if (!clickable) return;
    event.preventDefault();
    event.stopPropagation();
    onOsClick(os, event);
  };

  return (
    <div
      ref={ref}
      className={stripClass}
      style={{ left: `${leftPct}%`, width: `${widthPct}%` }}
      title={title}
      aria-label={title}
      onClick={clickable ? handleClick : undefined}
      onKeyDown={clickable ? (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        handleClick(event);
      } : undefined}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
    >
      {showOs ? (
        <span
          className={
            isAlmoco
              ? 'monitoramento-externo-os-em-almoco-strip-os'
              : isDesloc
                ? 'monitoramento-externo-os-em-deslocamento-strip-os'
                : 'monitoramento-externo-os-em-servico-strip-os'
          }
        >
          <ActivityIcon name={isAlmoco ? 'coffee' : isDesloc ? 'car' : 'headset'} size={10} />
          <span className="monitoramento-externo-os-strip-os-num">{displayLabel}</span>
          {clickable && showMoreHint && !isAlmoco ? (
            <span className="monitoramento-externo-os-strip-more">. . .</span>
          ) : null}
        </span>
      ) : null}
    </div>
  );
}

function MonitoramentoOsAndamentoBadges({ items, tempoKey, tempoTitle, badgeClass, tempoClass, spinnerClass, pillClass }) {
  void badgeClass;
  void tempoClass;
  void spinnerClass;
  void pillClass;
  const chipVariant = tempoKey === 'tempoEmServico' ? 'servico' : 'deslocamento';
  if (!items?.length) return null;
  return (
    <div className="monitoramento-externo-os-andamento-badges">
      {items.map((item) => {
        const os = typeof item === 'string' ? item : item?.os;
        const tempo = typeof item === 'object' && item != null ? item[tempoKey] : null;
        if (!os) return null;
        return (
          <span
            key={os}
            className={`monitoramento-externo-os-chip monitoramento-externo-os-chip--${chipVariant}`}
            title={tempo ? `OS ${os} • ${tempoTitle}: ${tempo}` : `OS ${os}`}
          >
            <span className="monitoramento-externo-os-chip-code">{os}</span>
            {tempo ? (
              <>
                <svg
                  className="monitoramento-externo-os-chip-icon"
                  width="10"
                  height="10"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.4"
                  aria-hidden="true"
                  focusable="false"
                >
                  <circle cx="12" cy="12" r="9" />
                  <path d="M12 7v5l3 3" />
                </svg>
                <span className="monitoramento-externo-os-chip-timer">{tempo}</span>
              </>
            ) : null}
          </span>
        );
      })}
    </div>
  );
}

function MonitoramentoActivity({ act, type, config, leftPct, widthPct, onOsClick }) {
  const ref = useRef(null);
  const tipRef = useRef(null);
  const [showLabel, setShowLabel] = useState(false);
  const [deslocTip, setDeslocTip] = useState(null);

  const isDeslocTrajeto = type === 'deslocamento_trajeto';
  const isAlmoco = type === 'almoco';
  const condOperacao = !isDeslocTrajeto && !isAlmoco && isFilled(act?.condOperacao);
  const condRetorno = !isDeslocTrajeto && !isAlmoco && isFilled(act?.condRetorno);

  const variant = condOperacao ? 'cond-operacao' : (condRetorno ? 'cond-retorno' : 'ok');
  const iconName = isAlmoco
    ? 'coffee'
    : isDeslocTrajeto
      ? 'car'
      : (condOperacao ? 'wrench' : (condRetorno ? 'bell' : 'circle-check'));

  const label = act?.label ? String(act.label) : '';
  const labelText = label && label.length > 12 ? label.slice(0, 12) + '…' : label;
  const isClickable = type === 'concluida' && label && typeof onOsClick === 'function';
  const tipKey = isDeslocTrajeto
    ? `desloc|${label}|${act?.start}|${act?.end}`
    : isAlmoco
      ? `almoco|${act?.start}|${act?.end}|${act?.inicioAlmoco || ''}`
      : '';

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const calc = () => {
      const w = el.clientWidth || 0;
      setShowLabel(Boolean(label) && w >= 48);
    };

    calc();

    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => calc());
    ro.observe(el);
    return () => ro.disconnect();
  }, [label]);

  useEffect(() => {
    if (!deslocTip) return;
    const onDoc = (ev) => {
      if (ref.current && ref.current.contains(ev.target)) return;
      if (tipRef.current && tipRef.current.contains(ev.target)) return;
      setDeslocTip(null);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [deslocTip]);

  const handleClick = (e) => {
    if (isDeslocTrajeto || isAlmoco) {
      e.preventDefault();
      e.stopPropagation();
      const target = e.currentTarget || ref.current;
      if (!target) return;
      const r = target.getBoundingClientRect();
      setDeslocTip((prev) => {
        if (prev && prev.key === tipKey) return null;
        if (isAlmoco) {
          const ini = act?.inicioAlmoco;
          const fim = act?.fimAlmoco;
          const tIni = ini != null ? formatLunchTimeHhMm(ini) : '—';
          const tFim = fim != null ? formatLunchTimeHhMm(fim) : (act?.open ? 'agora' : '—');
          const dur = fim != null ? formatDeslocDuracaoHhMm(ini, fim) : null;
          const intervalo =
            fim != null
              ? `${tIni} ~ ${tFim}${dur ? ` · ${dur}` : ''}`
              : `${tIni}${act?.open ? ' → em andamento' : ''}`;
          return {
            key: tipKey,
            kind: 'almoco',
            left: r.left,
            top: r.bottom + 8,
            title: 'Almoço',
            intervalo
          };
        }
        const ini = act?.inicioDeslocamento;
        const fim = act?.fimDeslocamento;
        const tIni = ini != null ? formatTimeHhMm(ini) : '—';
        const tFim = fim != null ? formatTimeHhMm(fim) : '—';
        const dur = formatDeslocDuracaoHhMm(ini, fim);
        const horarioText = `Horário: ${tIni} ~ ${tFim} > ${dur}`;
        return {
          key: tipKey,
          kind: 'desloc',
          left: r.left,
          top: r.bottom + 8,
          os: label || '—',
          cliente: isFilled(act?.cliente) ? String(act.cliente) : '—',
          horarioText
        };
      });
      return;
    }
    if (isClickable) {
      e.preventDefault();
      e.stopPropagation();
      onOsClick(label, e);
    }
  };

  const showClickable = isClickable || isDeslocTrajeto || isAlmoco;

  const onKeyDown = (e) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    if (isDeslocTrajeto || isAlmoco) {
      e.preventDefault();
      handleClick(e);
      return;
    }
    if (isClickable) {
      e.preventDefault();
      onOsClick(label, e);
    }
  };

  return (
    <>
    <div
      ref={ref}
      className={[
        'monitoramento-externo-activity',
        `monitoramento-externo-activity--${type}`,
        `monitoramento-externo-activity--${variant}`,
        showLabel ? '' : 'monitoramento-externo-activity--only-icon',
        showClickable ? 'monitoramento-externo-activity--clickable' : ''
      ].filter(Boolean).join(' ')}
      style={{ left: `${leftPct}%`, width: `${widthPct}%` }}
      title={isDeslocTrajeto || isAlmoco ? '' : (label || config.label)}
      onClick={handleClick}
      onKeyDown={showClickable ? onKeyDown : undefined}
      role={showClickable ? 'button' : undefined}
      tabIndex={showClickable ? 0 : undefined}
    >
      <span className="monitoramento-externo-activity-content">
        {showLabel ? <span className="monitoramento-externo-activity-label">{labelText}</span> : null}
        <span className="monitoramento-externo-activity-icon" aria-hidden="true">
          <ActivityIcon name={iconName} />
        </span>
      </span>
    </div>
    {deslocTip && createPortal(
      <div
        ref={tipRef}
        className={`monitoramento-externo-desloc-tip${deslocTip.kind === 'almoco' ? ' monitoramento-externo-desloc-tip--almoco' : ''}`}
        style={{
          position: 'fixed',
          left: Math.max(12, Math.min(deslocTip.left, typeof window !== 'undefined' ? window.innerWidth - 320 - 12 : deslocTip.left)),
          top: deslocTip.top,
          zIndex: 20060
        }}
        role="tooltip"
      >
        {deslocTip.kind === 'almoco' ? (
          <>
            <div className="monitoramento-externo-desloc-tip-header">
              <div className="monitoramento-externo-desloc-tip-header-left">
                <span className="monitoramento-externo-desloc-tip-kicker">Almoço</span>
                <span className="monitoramento-externo-desloc-tip-os">{deslocTip.title}</span>
              </div>
            </div>
            <div className="monitoramento-externo-desloc-tip-body">
              <div className="monitoramento-externo-desloc-tip-section">
                <div className="monitoramento-externo-desloc-tip-label">Intervalo</div>
                <div className="monitoramento-externo-desloc-tip-horario">{deslocTip.intervalo}</div>
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="monitoramento-externo-desloc-tip-header">
              <div className="monitoramento-externo-desloc-tip-header-left">
                <span className="monitoramento-externo-desloc-tip-kicker">Deslocamento</span>
                <span className="monitoramento-externo-desloc-tip-os">OS {deslocTip.os}</span>
              </div>
            </div>
            <div className="monitoramento-externo-desloc-tip-body">
              <div className="monitoramento-externo-desloc-tip-section">
                <div className="monitoramento-externo-desloc-tip-label">Cliente</div>
                <div className="monitoramento-externo-desloc-tip-cliente">{deslocTip.cliente}</div>
              </div>
              <div className="monitoramento-externo-desloc-tip-section">
                <div className="monitoramento-externo-desloc-tip-label">Intervalo</div>
                <div className="monitoramento-externo-desloc-tip-horario">{deslocTip.horarioText.replace(/^Horário:\s*/, '')}</div>
              </div>
            </div>
          </>
        )}
      </div>,
      document.body
    )}
    </>
  );
}

export function MonitoramentoExternoDashboard({
  showTitle = false,
  title = 'Monitoramento Externo',
  tvCountdown = null,
  panelId = null,
  configRefreshKey = 0,
  selectedDate = null,
  isLive = true,
  statusFilter = null,
  onStatusOptionsChange = null,
  exportRef = null,
  exportDataRef = null
}) {
  const [data, setData] = useState(() => ({
    kpis: INITIAL_KPIS,
    technicians: [],
    tempoMedioGeral: null,
    tempoMedioDeslocamentoGeral: null
  }));
  const [panelTechIds, setPanelTechIds] = useState([]);
  const [techIdToNome, setTechIdToNome] = useState({});
  const [horaAtual, setHoraAtual] = useState(formatHoraAtual);
  const [dataLoading, setDataLoading] = useState(false);
  const eventSourceRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const authFailedRef = useRef(false);
  const reconnectAttemptsRef = useRef(0);

  const [popover, setPopover] = useState({
    open: false,
    anchorRect: null,
    numeroOS: null,
    kind: 'detalhe',
    context: null,
    data: null,
    loading: false,
    error: null
  });
  const popoverRef = useRef(null);
  const baseUrl = getApiBaseUrl();

  const closePopover = useCallback(() => {
    setPopover((p) => ({
      ...p,
      open: false,
      anchorRect: null,
      data: null,
      error: null,
      loading: false,
      context: null,
      kind: 'detalhe'
    }));
  }, []);

  const forceLogoutAndRedirect = useCallback(() => {
    authFailedRef.current = true;
    try {
      clearAuthSession();
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('userData');
    } catch (_) {}

    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }

    if (eventSourceRef.current) {
      try {
        eventSourceRef.current.close();
      } catch (_) {}
      eventSourceRef.current = null;
    }

    if (typeof window !== 'undefined') {
      window.location.href = '/';
    }
  }, []);

  const handleOsClick = useCallback((numeroOS, event, options = {}) => {
    const target = event && event.currentTarget;
    const anchorRect = target && target.getBoundingClientRect ? target.getBoundingClientRect() : null;
    const kind = options.kind || 'detalhe';
    setPopover({
      open: true,
      anchorRect,
      numeroOS,
      kind,
      context: options.context || null,
      data: null,
      loading: true,
      error: null
    });
  }, []);

  useEffect(() => {
    if (!popover.open || !popover.numeroOS) return;
    const token = typeof localStorage !== 'undefined' ? localStorage.getItem('authToken') : null;
    if (!token) {
      setPopover((p) => ({ ...p, loading: false, error: 'Sessão não encontrada' }));
      return;
    }
    const isResumo = popover.kind === 'resumo' || popover.kind === 'deslocamento';
    const path = isResumo
      ? `/api/monitoramento/externo/os/${encodeURIComponent(popover.numeroOS)}/resumo`
      : `/api/monitoramento/externo/os/${encodeURIComponent(popover.numeroOS)}`;
    const url = `${baseUrl}${path}`;
    const controller = new AbortController();
    fetch(url, {
      headers: { Authorization: `Bearer ${token}` },
      signal: controller.signal
    })
      .then((res) => {
        if (!res.ok) {
          if (res.status === 404) return { success: false, message: 'OS não encontrada' };
          return res.json().then((b) => b).catch(() => ({ success: false, message: res.statusText }));
        }
        return res.json();
      })
      .then((json) => {
        if (json.success && json.data) {
          if (popover.kind === 'resumo') {
            setPopover((p) => ({
              ...p,
              data: buildOsResumoDisplay(p.numeroOS, json.data, p.context),
              loading: false,
              error: null
            }));
          } else if (popover.kind === 'deslocamento') {
            setPopover((p) => ({
              ...p,
              data: json.data,
              loading: false,
              error: null
            }));
          } else {
            setPopover((p) => ({ ...p, data: json.data, loading: false, error: null }));
          }
        } else {
          setPopover((p) => ({
            ...p,
            loading: false,
            error: json.message || 'Erro ao carregar detalhe'
          }));
        }
      })
      .catch((err) => {
        if (err.name === 'AbortError') return;
        setPopover((p) => ({
          ...p,
          loading: false,
          error: err.message || 'Erro de conexão'
        }));
      });
    return () => controller.abort();
  }, [popover.open, popover.numeroOS, popover.kind, baseUrl]);

  useEffect(() => {
    if (!popover.open) return;
    const onMouseDown = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) {
        closePopover();
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [popover.open, closePopover]);

  useEffect(() => {
    if (!isLive) return undefined;
    const t = setInterval(() => setHoraAtual(formatHoraAtual()), 1000);
    return () => clearInterval(t);
  }, [isLive]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = typeof localStorage !== 'undefined' ? localStorage.getItem('authToken') : null;
        if (!token) return;
        const res = await fetch(`${baseUrl}/api/team/technicians`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const json = await res.json();
        if (cancelled || !json.data || !Array.isArray(json.data)) return;
        const map = {};
        json.data.forEach((t) => {
          if (t && t.id != null) map[String(t.id)] = t.nome || t.name || String(t.id);
        });
        setTechIdToNome(map);
      } catch (_) {
        if (!cancelled) setTechIdToNome({});
      }
    })();
    return () => { cancelled = true; };
  }, [baseUrl]);

  useEffect(() => {
    if (panelId == null) {
      setPanelTechIds([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const token = typeof localStorage !== 'undefined' ? localStorage.getItem('authToken') : null;
        if (!token) {
          if (!cancelled) setPanelTechIds([]);
          return;
        }
        const res = await fetch(`${baseUrl}/api/monitoramento/externo/config/${encodeURIComponent(panelId)}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const json = await res.json();
        if (cancelled) return;
        if (json.success && json.data && Array.isArray(json.data.tecnicos) && json.data.tecnicos.length > 0) {
          setPanelTechIds(json.data.tecnicos.map((id) => String(id)));
        } else {
          setPanelTechIds([]);
        }
      } catch (_) {
        if (!cancelled) setPanelTechIds([]);
      }
    })();
    return () => { cancelled = true; };
  }, [baseUrl, panelId, configRefreshKey]);

  useEffect(() => {
    if (isLive) return undefined;

    let cancelled = false;
    const controller = new AbortController();

    async function loadHistorical() {
      const token = typeof localStorage !== 'undefined' ? localStorage.getItem('authToken') : null;
      if (!token) return;

      setDataLoading(true);
      try {
        const params = new URLSearchParams();
        if (panelId != null) params.set('panelId', String(panelId));
        if (selectedDate) params.set('data', selectedDate);
        const res = await fetch(`${baseUrl}/api/monitoramento/externo/dashboard?${params.toString()}`, {
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal
        });
        const json = await res.json();
        if (cancelled || !json.success || !json.data) return;
        const next = json.data;
        setData({
          kpis: next.kpis || INITIAL_KPIS,
          technicians: next.technicians || [],
          tempoMedioGeral: next.tempoMedioGeral ?? null,
          tempoMedioDeslocamentoGeral: next.tempoMedioDeslocamentoGeral ?? null
        });
      } catch (err) {
        if (err.name === 'AbortError') return;
      } finally {
        if (!cancelled) setDataLoading(false);
      }
    }

    loadHistorical();
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [isLive, selectedDate, panelId, baseUrl, configRefreshKey]);

  useEffect(() => {
    if (!isLive) {
      if (eventSourceRef.current) {
        try {
          eventSourceRef.current.close();
        } catch (_) {}
        eventSourceRef.current = null;
      }
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      return undefined;
    }

    let cancelled = false;

    const base = baseUrl;
    const BASE_RECONNECT_DELAY_MS = 4000;

    function scheduleReconnect() {
      if (cancelled) return;
      if (authFailedRef.current) return;
      if (reconnectTimerRef.current) return; // evita schedules duplicados

      reconnectAttemptsRef.current += 1;
      const attempt = reconnectAttemptsRef.current;

      const delay = Math.min(
        60000,
        Math.round(BASE_RECONNECT_DELAY_MS * Math.pow(2, attempt - 1))
      );

      reconnectTimerRef.current = setTimeout(() => {
        reconnectTimerRef.current = null;
        connect();
      }, delay);
    }

    async function connect() {
      if (cancelled) return;
      if (authFailedRef.current) return;

      if (!hasAuthSession()) {
        forceLogoutAndRedirect();
        return;
      }

      // Gera cookie HttpOnly (subtoken) para o SSE; access JWT vem do cookie de sessão.
      try {
        const res = await fetch(`${base}/api/monitoramento/externo/stream-token`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
        });

        if (res.status === 401) {
          forceLogoutAndRedirect();
          return;
        }

        if (!res.ok) {
          scheduleReconnect();
          return;
        }
      } catch (_) {
        scheduleReconnect();
        return;
      }

      if (cancelled) return;
      if (authFailedRef.current) return;

      reconnectAttemptsRef.current = 0;

      let url = `${base}/api/monitoramento/externo/stream`;
      if (panelId != null) url += `?panelId=${encodeURIComponent(panelId)}`;

      // Garante que nao existe EventSource antigo rodando.
      if (eventSourceRef.current) {
        try {
          eventSourceRef.current.close();
        } catch (_) {}
        eventSourceRef.current = null;
      }

      const es = new EventSource(url, { withCredentials: true });
      eventSourceRef.current = es;

      es.onopen = () => {
        reconnectAttemptsRef.current = 0;
      };

      es.onmessage = (e) => {
        try {
          const next = JSON.parse(e.data);
          if (next.error) return;
          setData((prev) => ({
            kpis: next.kpis || prev.kpis,
            technicians: next.technicians || prev.technicians,
            tempoMedioGeral: next.tempoMedioGeral ?? prev.tempoMedioGeral,
            tempoMedioDeslocamentoGeral: next.tempoMedioDeslocamentoGeral ?? prev.tempoMedioDeslocamentoGeral
          }));
        } catch (_) {}
      };

      es.onerror = () => {
        try {
          es.close();
        } catch (_) {}

        if (eventSourceRef.current === es) {
          eventSourceRef.current = null;
        }

        scheduleReconnect();
      };
    }

    connect();

    return () => {
      cancelled = true;
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
    };
  }, [panelId, baseUrl, forceLogoutAndRedirect, isLive]);

  const { kpis, technicians, tempoMedioGeral, tempoMedioDeslocamentoGeral } = data;
  const sortedTechnicians = useMemo(() => sortTechnicians(technicians, isLive), [technicians, isLive]);
  const displayTechnicians = useMemo(() => {
    if (!panelTechIds.length) return sortedTechnicians;
    const inStream = new Set(sortedTechnicians.map((t) => String(t.id)));
    const seenExtra = new Set();
    const extras = [];
    panelTechIds.forEach((rawId) => {
      const id = String(rawId);
      if (inStream.has(id) || seenExtra.has(id)) return;
      seenExtra.add(id);
      extras.push({
        id,
        nome: techIdToNome[id] || `Técnico ${id}`,
        status: 'nao_alocado',
        sla: '—',
        osCount: 0,
        totalOs: 0,
        tempoProduzido: null,
        tempoMedioDeslocamento: '—',
        tempoTotalDeslocamento: '—',
        activities: [],
        osEmServico: [],
        osEmDeslocamento: []
      });
    });
    return [...sortedTechnicians, ...extras];
  }, [sortedTechnicians, panelTechIds, techIdToNome]);

  useEffect(() => {
    if (typeof onStatusOptionsChange !== 'function') return;
    const ids = new Set();
    displayTechnicians.forEach((tec) => {
      ids.add(getTechnicianDisplayStatus(tec, isLive));
    });
    const options = [...ids]
      .sort((a, b) => statusSortRank(a) - statusSortRank(b))
      .map((id) => ({
        id,
        label: TECH_STATUS_TYPES[id]?.label || STATUS_TYPES[id]?.label || id
      }));
    onStatusOptionsChange(options);
  }, [displayTechnicians, isLive, onStatusOptionsChange]);

  const filteredTechnicians = useMemo(() => {
    if (!statusFilter || statusFilter.length === 0) return displayTechnicians;
    const allowed = new Set(statusFilter);
    return displayTechnicians.filter((tec) => allowed.has(getTechnicianDisplayStatus(tec, isLive)));
  }, [displayTechnicians, statusFilter, isLive]);

  useEffect(() => {
    if (!exportDataRef) return;
    exportDataRef.current = {
      panelTitle: title,
      selectedDate,
      isLive,
      kpis,
      tempoMedioGeral,
      tempoMedioDeslocamentoGeral,
      horaAtual,
      technicians: filteredTechnicians.map((tec) => ({
        ...tec,
        displayStatus: getTechnicianDisplayStatus(tec, isLive)
      }))
    };
  }, [
    exportDataRef,
    title,
    selectedDate,
    isLive,
    kpis,
    tempoMedioGeral,
    tempoMedioDeslocamentoGeral,
    horaAtual,
    filteredTechnicians
  ]);

  const tempoMedioAtendimento = tempoMedioGeral != null && tempoMedioGeral !== '' ? tempoMedioGeral : mediaTempoMedioAtendimento(technicians);
  const nowPct = useMemo(() => getNowPositionPct(), [horaAtual]);

  return (
    <div
      ref={exportRef}
      className={`monitoramento-externo-dashboard${!isLive ? ' monitoramento-externo-dashboard--historical' : ''}${dataLoading ? ' monitoramento-externo-dashboard--loading' : ''}`}
    >
      <header className="monitoramento-externo-kpis">
        {showTitle && (
          <div className="monitoramento-externo-title-row">
            <div className="monitoramento-externo-title">{title}</div>
            {tvCountdown ? (
              <span className="monitoramento-tv-countdown" aria-live="polite" title="Próxima troca de painel">
                {tvCountdown}
              </span>
            ) : null}
          </div>
        )}
        <MonitoramentoKpiCard
          icon={ContactRound}
          value={(
            <>
              <span className="monitoramento-externo-kpi-value-highlight monitoramento-externo-kpi-value-highlight--teal">{kpis.ativos}</span>
              /{kpis.total}
            </>
          )}
          label={`Técnicos ativos — ${kpis.ativosLabel}`}
        />
        <MonitoramentoKpiCard
          icon={CalendarCheck}
          value={`${kpis.osConcluidas}/${kpis.meta}`}
          extra={
            kpis.retornosDia > 0 ? (
              <>
                retornos (<span className="monitoramento-externo-kpi-extra-num">{kpis.retornosDia}</span>)
              </>
            ) : null
          }
          label={`OS concluídas — ${kpis.metaPct}% do roteiro`}
        />
        <MonitoramentoKpiCard
          icon={Hourglass}
          value={String(kpis.osSemEncaminhamento)}
          label="OS abertas sem encaminhamento"
          valueTone="accent"
        />
        <MonitoramentoKpiCard
          icon={Timer}
          value={tempoMedioAtendimento}
          label="Tempo médio de atendimento"
        />
        <MonitoramentoKpiCard
          icon={Car}
          value={tempoMedioDeslocamentoGeral != null && tempoMedioDeslocamentoGeral !== '' ? tempoMedioDeslocamentoGeral : '—'}
          label="Tempo médio de deslocamento"
        />
        <MonitoramentoKpiCard
          icon={Watch}
          value={horaAtual}
          label="Hora atual"
        />
      </header>

      <div className="monitoramento-externo-table-wrap">
        <div className="monitoramento-externo-table-scroll">
          <div className="monitoramento-externo-grid-header">
            <div className="monitoramento-externo-grid-cell monitoramento-externo-grid-cell--tecnico">Técnico</div>
            <div className="monitoramento-externo-grid-cell monitoramento-externo-grid-cell--status">Status</div>
            <div className="monitoramento-externo-grid-cell monitoramento-externo-grid-cell--tempo">Tempo médio</div>
            <div className="monitoramento-externo-grid-cell monitoramento-externo-grid-cell--progresso">Progresso</div>
            <div className="monitoramento-externo-grid-cell monitoramento-externo-grid-cell--timeline" aria-hidden="true">
              {HOURS.map((h) => (
                <span
                  key={h}
                  className="monitoramento-externo-hour-mark"
                  style={getHourMarkStyle(h)}
                >
                  {h}H
                </span>
              ))}
            </div>
            <div className="monitoramento-externo-grid-cell monitoramento-externo-grid-cell--desloc">Desloc.</div>
            <div className="monitoramento-externo-grid-cell monitoramento-externo-grid-cell--produzido">Produzido</div>
          </div>

          {filteredTechnicians.map((tec) => {
            const progressPct = tec.totalOs > 0 ? Math.round((tec.osCount / tec.totalOs) * 100) : 0;
            const activities = tec.activities || [];
            const hasCondRetorno = activities.some((a) => isFilled(a.condRetorno));
            const hasCondOperacao = activities.some((a) => isFilled(a.condOperacao));
            const osOperacao = collectOsByCondition(activities, 'condOperacao');
            const osRetorno = collectOsByCondition(activities, 'condRetorno');
            const displayStatus = getTechnicianDisplayStatus(tec, isLive);
            const statusMeta = STATUS_TYPES[displayStatus] || {};
            const produzidoDisplay = tec.osCount > 0 ? (tec.tempoProduzido ?? '—') : '—';
            const hasProduzido = produzidoDisplay !== '—';
            const osEmServico = isLive && tec.status === 'em_servico' && Array.isArray(tec.osEmServico) ? tec.osEmServico : [];
            const osEmDeslocamento = isLive && tec.status === 'deslocamento' && Array.isArray(tec.osEmDeslocamento) ? tec.osEmDeslocamento : [];

            return (
              <div key={tec.id} className="monitoramento-externo-grid-row">
                <div className="monitoramento-externo-grid-cell monitoramento-externo-grid-cell--tecnico">
                  <div className="monitoramento-externo-tec-avatar" aria-hidden>
                    {getTechnicianInitials(tec.nome)}
                  </div>
                  <div className="monitoramento-externo-tec-info">
                    <div className="monitoramento-externo-tec-name-row">
                      <span className="monitoramento-externo-tec-name" title={tec.nome}>{formatTechnicianDisplayName(tec.nome)}</span>
                      {(hasCondOperacao || hasCondRetorno) && (
                        <div className="monitoramento-externo-indicators-wrap">
                          {hasCondOperacao && (
                            <MonitoramentoModernTooltip
                              label="Fechamento de OS com pendência de peça"
                              osList={osOperacao}
                              placement="top"
                            >
                              <span className="monitoramento-externo-indicator monitoramento-externo-indicator--cond-operacao">
                                <ActivityIcon name="wrench" size={10} />
                              </span>
                            </MonitoramentoModernTooltip>
                          )}
                          {hasCondRetorno && (
                            <MonitoramentoModernTooltip
                              label="Fechamento de OS com necessidade de retorno"
                              osList={osRetorno}
                              placement="top"
                            >
                              <span className="monitoramento-externo-indicator monitoramento-externo-indicator--cond-retorno">
                                <ActivityIcon name="bell" size={10} />
                              </span>
                            </MonitoramentoModernTooltip>
                          )}
                        </div>
                      )}
                    </div>
                    {osEmServico.length > 0 && (
                      <MonitoramentoOsAndamentoBadges
                        items={osEmServico}
                        tempoKey="tempoEmServico"
                        tempoTitle="Tempo em serviço"
                        badgeClass="monitoramento-externo-os-em-servico-badge"
                        tempoClass="monitoramento-externo-os-em-servico-tempo"
                        spinnerClass="monitoramento-externo-os-em-servico-tempo-spinner"
                        pillClass="monitoramento-externo-os-andamento-pill--servico"
                      />
                    )}
                    {osEmDeslocamento.length > 0 && (
                      <MonitoramentoOsAndamentoBadges
                        items={osEmDeslocamento}
                        tempoKey="tempoEmDeslocamento"
                        tempoTitle="Tempo em deslocamento"
                        badgeClass="monitoramento-externo-os-em-deslocamento-badge"
                        tempoClass="monitoramento-externo-os-em-deslocamento-tempo"
                        spinnerClass="monitoramento-externo-os-em-deslocamento-tempo-spinner"
                        pillClass="monitoramento-externo-os-andamento-pill--deslocamento"
                      />
                    )}
                  </div>
                </div>

                <div className="monitoramento-externo-grid-cell monitoramento-externo-grid-cell--status">
                  <div className="monitoramento-externo-tec-status">
                    <span
                      className={`monitoramento-externo-tec-status-dot ${displayStatus === 'em_servico' || displayStatus === 'deslocamento' ? 'monitoramento-externo-tec-status-dot--pulse' : ''} ${displayStatus === 'ocioso' ? 'monitoramento-externo-tec-status-dot--pulse-ocioso' : ''} ${displayStatus === 'almoco' ? 'monitoramento-externo-tec-status-dot--pulse-almoco' : ''} ${displayStatus === 'nao_alocado' ? 'monitoramento-externo-tec-status-dot--nao-alocado' : ''}`}
                      style={{ background: statusMeta.dot || '#94a3b8' }}
                    />
                    <span className="monitoramento-externo-tec-status-label" style={{ color: statusMeta.color || '#4B5563' }}>
                      {statusMeta.label || displayStatus}
                    </span>
                  </div>
                </div>

                <div className={`monitoramento-externo-grid-cell monitoramento-externo-grid-cell--tempo${tec.sla && tec.sla !== '—' ? '' : ' monitoramento-externo-grid-cell--muted'}`}>
                  {tec.sla || '—'}
                </div>

                <div className="monitoramento-externo-grid-cell monitoramento-externo-grid-cell--progresso">
                  <div className="monitoramento-externo-progresso-track" role="progressbar" aria-valuenow={tec.totalOs ? progressPct : 0} aria-valuemin={0} aria-valuemax={100} aria-label={`Progresso ${tec.osCount}/${tec.totalOs}`}>
                    <div className="monitoramento-externo-progresso-fill" style={{ width: `${progressPct}%` }} />
                  </div>
                  <div className="monitoramento-externo-progresso-label">{tec.osCount}/{tec.totalOs}</div>
                </div>

                <div className="monitoramento-externo-grid-cell monitoramento-externo-grid-cell--timeline">
                  <div className="monitoramento-externo-hours-strip">
                    {isLive ? (
                      <div className="monitoramento-externo-now-line" style={{ left: `${nowPct}%` }} aria-hidden="true" />
                    ) : null}
                    {isLive && osEmServico.map((item) => {
                      const os = typeof item === 'string' ? item : item?.os;
                      const dataInicio = typeof item === 'object' && item != null ? item.dataInicioServico : null;
                      const tempo = typeof item === 'object' && item != null ? item.tempoEmServico : null;
                      if (!os || !dataInicio) return null;
                      return (
                        <MonitoramentoOsAndamentoBar
                          key={`em-servico-${os}`}
                          variant="em_servico"
                          os={os}
                          dataInicio={dataInicio}
                          tempo={tempo}
                          nowTick={horaAtual}
                            onOsClick={(numeroOS, event) => handleOsClick(numeroOS, event, {
                              kind: 'resumo',
                              context: { dataInicio, tempo, statusLabel: 'Em serviço' }
                            })}
                        />
                      );
                    })}
                    {isLive && osEmDeslocamento.map((item) => {
                      const os = typeof item === 'string' ? item : item?.os;
                      const dataInicio = typeof item === 'object' && item != null ? item.dataInicioDeslocamento : null;
                      const tempo = typeof item === 'object' && item != null ? item.tempoEmDeslocamento : null;
                      if (!os || !dataInicio) return null;
                      return (
                        <MonitoramentoOsAndamentoBar
                          key={`deslocamento-${os}`}
                          variant="deslocamento"
                          os={os}
                          dataInicio={dataInicio}
                          tempo={tempo}
                          nowTick={horaAtual}
                            onOsClick={(numeroOS, event) => handleOsClick(numeroOS, event, {
                              kind: 'deslocamento',
                              context: { dataInicio, tempo }
                            })}
                        />
                      );
                    })}
                    {isLive &&
                      (() => {
                        // Almoço em andamento: barra crescente (igual desloc./serviço)
                        const open =
                          tec.almocoAberto ||
                          (activities || []).find((a) => a?.type === 'almoco' && a.open && a.inicioAlmoco);
                        const inicio = open?.inicioAlmoco || null;
                        if (!inicio) return null;
                        void horaAtual;
                        const tempoAlmoco = formatDeslocDuracaoHhMm(inicio, new Date());
                        return (
                          <MonitoramentoOsAndamentoBar
                            key="almoco-aberto"
                            variant="almoco"
                            os="Almoço"
                            label="Almoço"
                            dataInicio={inicio}
                            tempo={tempoAlmoco !== '—' ? tempoAlmoco : null}
                            nowTick={horaAtual}
                          />
                        );
                      })()}
                    {activities.map((act, idx) => {
                      const type = act.type;
                      // aberto: já renderizado na barra live acima
                      if (type === 'almoco' && act.open) return null;
                      const config = type ? STATUS_TYPES[type] : null;
                      if (!config) return null;
                      const TOTAL_MIN = (HOUR_END - HOUR_START) * 60;
                      let startClamp = Math.max(0, Number(act.start));
                      let endClamp = Math.min(TOTAL_MIN, Number(act.end));
                      if (type === 'almoco' && act.inicioAlmoco) {
                        const s = minutesFrom7hSaoPauloInstant(act.inicioAlmoco);
                        const e = act.fimAlmoco ? minutesFrom7hSaoPauloInstant(act.fimAlmoco) : null;
                        if (s != null) startClamp = Math.max(0, Math.min(TOTAL_MIN, s));
                        if (e != null) endClamp = Math.max(startClamp + 0.5, Math.min(TOTAL_MIN, e));
                      }
                      if (!Number.isFinite(startClamp) || !Number.isFinite(endClamp)) return null;
                      if (endClamp <= startClamp) endClamp = Math.min(TOTAL_MIN, startClamp + 1);
                      if (startClamp >= endClamp) return null;
                      const leftPct = (startClamp / TOTAL_MIN) * 100;
                      const widthPct = ((endClamp - startClamp) / TOTAL_MIN) * 100;
                      return (
                        <MonitoramentoActivity
                          key={`${type}-${act.start}-${act.end}-${idx}`}
                          act={act}
                          type={type}
                          config={config}
                          leftPct={leftPct}
                          widthPct={widthPct}
                          onOsClick={handleOsClick}
                        />
                      );
                    })}
                  </div>
                </div>

                <div className={`monitoramento-externo-grid-cell monitoramento-externo-grid-cell--desloc${tec.tempoMedioDeslocamento || tec.tempoTotalDeslocamento ? '' : ' monitoramento-externo-grid-cell--muted'}`}>
                  {tec.tempoMedioDeslocamento != null && tec.tempoMedioDeslocamento !== '' && tec.tempoMedioDeslocamento !== '—' ? (
                    <MonitoramentoModernTooltip
                      label="Tempo médio de deslocamento"
                      placement="left"
                      className="monitoramento-modern-tooltip-host--desloc"
                    >
                      <div className="monitoramento-externo-desloc-line">
                        <span className="monitoramento-externo-desloc-prefix">M</span>
                        <span>{tec.tempoMedioDeslocamento}</span>
                      </div>
                    </MonitoramentoModernTooltip>
                  ) : (
                    <div className="monitoramento-externo-desloc-line">M —</div>
                  )}
                  {tec.tempoTotalDeslocamento != null && tec.tempoTotalDeslocamento !== '' && tec.tempoTotalDeslocamento !== '—' ? (
                    <MonitoramentoModernTooltip
                      label="Tempo total de deslocamento"
                      placement="left"
                      className="monitoramento-modern-tooltip-host--desloc"
                    >
                      <div className="monitoramento-externo-desloc-line">
                        <span className="monitoramento-externo-desloc-prefix">T</span>
                        <span>{tec.tempoTotalDeslocamento}</span>
                      </div>
                    </MonitoramentoModernTooltip>
                  ) : (
                    <div className="monitoramento-externo-desloc-line">T —</div>
                  )}
                </div>

                <div className={`monitoramento-externo-grid-cell monitoramento-externo-grid-cell--produzido${hasProduzido ? '' : ' monitoramento-externo-grid-cell--muted'}`}>
                  <div className="monitoramento-externo-produzido-value">{produzidoDisplay}</div>
                  <div className="monitoramento-externo-produzido-track">
                    <div
                      className="monitoramento-externo-produzido-fill"
                      style={{ width: hasProduzido ? `${progressPct}%` : '0%' }}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {popover.open && popover.kind === 'deslocamento' && (
        <DeslocamentoAndamentoPopover
          anchorRect={popover.anchorRect}
          numeroOS={popover.numeroOS}
          data={popover.data}
          loading={popover.loading}
          error={popover.error}
          context={popover.context}
          onClose={closePopover}
          popoverRef={popoverRef}
        />
      )}
      {popover.open && popover.kind !== 'deslocamento' && (
        <OsDetalhePopover
          anchorRect={popover.anchorRect}
          data={popover.data}
          loading={popover.loading}
          error={popover.error}
          onClose={closePopover}
          popoverRef={popoverRef}
        />
      )}
    </div>
  );
}
